import '../styles/checklist.css';
import { PCInstance } from '../types/database';

// What a Dagstart run will actually do, resolved from live DB state + a status
// sweep just before the operator confirms. Rendering the plan (instead of a
// generic "weet je het zeker?") is what makes the confirmation meaningful —
// restarting a PC is destructive if it turns out someone is still in there.
export interface DagstartPlan {
  // Off / unreachable PCs that get flagged for peer Wake-on-LAN.
  toWake: PCInstance[];
  // PCs that are already up and get rebooted.
  toRestart: PCInstance[];
  // The subset of toWake that has an LSC/Tuya smart plug configured
  // (infra.pc_power_control) and will be power-cycled out-of-band.
  gateways: PCInstance[];
}

interface DagstartProps {
  plan: DagstartPlan;
  busy: boolean;
  progress: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function pcLabel(pc: PCInstance): string {
  const name = pc.pc_name || pc.ip_address || pc.id;
  // Room NUCs appear in the wake list but nowhere else in the operator, so mark
  // them — otherwise an unfamiliar name reads as a stray row.
  return pc.device_kind === 'nuc' ? `${name} (NUC)` : name;
}

export default function Dagstart({ plan, busy, progress, onConfirm, onCancel }: DagstartProps) {
  const { toWake, toRestart, gateways } = plan;
  const steps: { title: string; content: string }[] = [];

  if (gateways.length > 0) {
    steps.push({
      title: 'Slimme stekker aanzetten',
      content:
        `De gateway-pc (${gateways.map(pcLabel).join(', ')}) staat uit en wordt via de slimme stekker ` +
        'uit- en weer aangezet. Die pc start op en wekt daarna de rest van het netwerk.',
    });
  }
  if (toWake.length > 0) {
    steps.push({
      title: `${toWake.length} pc${toWake.length === 1 ? '' : "'s"} aanzetten`,
      content: `Wake-on-LAN naar: ${toWake.map(pcLabel).join(', ')}.`,
    });
  }
  if (toRestart.length > 0) {
    steps.push({
      title: `${toRestart.length} pc${toRestart.length === 1 ? '' : "'s"} herstarten`,
      content: `Deze staan al aan en worden opnieuw opgestart: ${toRestart.map(pcLabel).join(', ')}.`,
    });
  }

  return (
    <div className="checklist-screen">
      <div className="checklist-modal">
        <div className="checklist-header">
          <h2>Dagstart</h2>
          <p>Controleer wat er gaat gebeuren en bevestig</p>
        </div>
        <div className="checklist-body">
          {steps.length === 0 ? (
            <div className="checklist-step">
              <div className="checklist-step-text">
                <div className="checklist-step-title">Niets te doen</div>
                <div className="checklist-step-content">
                  Er zijn geen pc's in deze ruimte om aan te zetten of te herstarten.
                </div>
              </div>
            </div>
          ) : (
            steps.map((step, index) => (
              <div key={index} className="checklist-step">
                <div className="checklist-step-text">
                  <div className="checklist-step-number">{index + 1}</div>
                  <div className="checklist-step-title">{step.title}</div>
                  <div className="checklist-step-content">{step.content}</div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="checklist-footer">
          {progress && <div className="checklist-progress">{progress}</div>}
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Annuleren
          </button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={busy || steps.length === 0}>
            {busy ? 'Bezig…' : 'Bevestig & Starten'}
          </button>
        </div>
      </div>
    </div>
  );
}
